import { routes } from "@/global/routes";
import { vendorUUIDs } from "../../constants";
import { howItWorks, faqs } from "./helpers";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";

import AuthBgBanner from "public/images/devils/devils.png";
import FindGamesBanner from "public/images/devils/1486X148.jpg";
import SeatMap3dPreview from "public/images/devils/devils_flex_3d_venue.png";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

const logoImage =
  "https://ss-leagues.s3.amazonaws.com/NHL_2024/NewJersey-Devils-2024.jpg";

const config: TeamConfig = {
  teamSchema: "devils_minipack",
  browserTitle: "New Jersey Devils 3 Game Pack",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "New Jersey Devils",

  buckets: {
    enabled: true,
    list: [
      {
        id: 1,
        min: 1,
        max: 1,
        name: "Tier 1",
        isPrimary: true,
        closeWhenHitMax: true,
        dontCloseWhenHitMin: true,
        description: "Please select one game from tier 1 below.",
        remainingBucketLanguage: "Select 3 games to complete your 3 Game Pack!",
      },
      {
        id: 2,
        min: 1,
        max: 1,
        name: "Tier 2",
        closeWhenHitMax: true,
        dontCloseWhenHitMin: true,
        description: "Please select one game from tier 2 below",
        remainingBucketLanguage:
          "Select 2 more games to complete your 3 Game Pack!",
      },
      {
        id: 3,
        min: 1,
        max: 1,
        name: "Tier 3",
        closeWhenHitMax: true,
        dontCloseWhenHitMin: true,
        description: "Please select one game from tier 3 below.",
        remainingBucketLanguage:
          "Select 1 more game to complete your 3 Game Pack!",
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10215-devils-sec",
    mapId: "main",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.purchaseSeats,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 3,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    checkoutTimerEnabled: false,
    showFrontendTotalAmount: false,
    showFrontendServiceFee: false,
    defaultSeatCount: 2,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
    checkoutTimerEnabled: false,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },
  isSaveCardAllowed: true,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "G-XT9NZ1847J",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "120085088485875",
    stackAdaptPixelId: "pmYksymwpGkOLia4azzaJQ",
  },

  // call refresh inventory when selecting games
  refreshInventoryWhenEventClick: false,

  shouldCheckZipCode: false,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/devils/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  // images
  images: {
    findGamesBannerImage: FindGamesBanner.src,
    authBackgroundImage: AuthBgBanner.src,
    checkoutStatusPageImage: AuthBgBanner.src,
    logoImage: `${logoImage}?width=64&height=64`,
    logoNameImage: "",
    faviconImage:
      "https://ss-leagues.s3.amazonaws.com/NHL/NewJerseyDevils-aug21.png",
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },

    hideCheckoutCartBalance: true,
  },

  colors: {
    primary: "#CE1126",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarSuccessText: "You're good to go! Checkout now.",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage:
      "Tickets are available in your New Jersey Devils Account Manager!",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },
    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
